/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";

export default styled(Box)(({ theme, ownerState }) => {
  const { palette, typography, functions } = theme;
  const { darkMode } = ownerState;

  const { white, dark, grey } = palette;
  const { pxToRem, rgba } = functions;

  return {
    minHeight: "32vh",

    "& .fc": {
      "--fc-border-color": "transparent",
      "--fc-page-bg-color": "transparent",
      "--fc-neutral-bg-color": "transparent",
      "--fc-list-event-hover-bg-color": "transparent",
    },

    "& .fc-media-screen": {
      color: dark.main,
    },

    "& .fc-theme-standard .fc-scrollgrid": {
      border: "none",
      borderRadius: pxToRem(28),
      overflow: "hidden",
    },

    "& .fc-theme-standard th, & .fc-theme-standard td": {
      borderColor: rgba(grey[400], 0.18),
    },

    "& .fc-scrollgrid-section-header > td": {
      borderBottom: "none",
    },

    "& .fc .fc-col-header-cell": {
      background: "transparent",
      paddingBottom: pxToRem(8),
    },

    "& .fc .fc-col-header-cell-cushion": {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: pxToRem(4),
      padding: `${pxToRem(12)} 0 ${pxToRem(8)}`,
      color: darkMode ? white.main : dark.main,
      fontSize: pxToRem(11),
      fontWeight: typography.fontWeightBold,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
    },

    "& .fc .fc-daygrid-day-top": {
      justifyContent: "center",
      paddingTop: pxToRem(12),
    },

    "& .fc .fc-daygrid-day-number": {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: pxToRem(68),
      minHeight: pxToRem(48),
      borderRadius: pxToRem(16),
      color: darkMode ? white.main : dark.main,
      fontSize: pxToRem(12),
      fontWeight: typography.fontWeightBold,
      lineHeight: 1.2,
      padding: `${pxToRem(7)} ${pxToRem(12)}`,
    },

    "& .fc .fc-daygrid-day.fc-day-today .fc-daygrid-day-number": {
      background: "linear-gradient(135deg, #7A4DFF 0%, #5E38D5 100%)",
      color: `${white.main} !important`,
      boxShadow: `0 18px 34px ${rgba("#7A4DFF", 0.28)}`,
      transform: "translateY(-1px) scale(1.03)",
    },

    "& .fc .fc-daygrid-day-frame": {
      height: pxToRem(320),
      minHeight: pxToRem(320),
      display: "flex",
      flexDirection: "column",
      backgroundColor: darkMode ? rgba(white.main, 0.03) : "#FFFFFF",
      transition: "transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease",
    },

    "& .fc .fc-daygrid-day-frame:hover": {
      transform: "translateY(-2px)",
      boxShadow: `0 20px 34px ${rgba(dark.main, darkMode ? 0.14 : 0.06)}`,
    },

    "& .fc .fc-day-today .fc-daygrid-day-frame": {
      background: darkMode
        ? `linear-gradient(180deg, ${rgba("#7A4DFF", 0.14)} 0%, ${rgba(white.main, 0.04)} 100%)`
        : "linear-gradient(180deg, #FBF8FF 0%, #F4EEFF 100%)",
      boxShadow: `inset 0 0 0 1px ${rgba("#7A4DFF", darkMode ? 0.34 : 0.18)}`,
    },

    "& .fc .fc-daygrid-day-events": {
      display: "flex",
      flexDirection: "column",
      flex: 1,
      gap: pxToRem(10),
      padding: `${pxToRem(12)} ${pxToRem(12)} ${pxToRem(14)}`,
      minHeight: 0,
      overflowY: "auto",
      overflowX: "hidden",
      scrollbarWidth: "thin",
      scrollbarColor: `${rgba("#7A4DFF", 0.35)} transparent`,
    },

    "& .fc .fc-daygrid-day-events::-webkit-scrollbar": {
      width: pxToRem(6),
    },

    "& .fc .fc-daygrid-day-events::-webkit-scrollbar-thumb": {
      backgroundColor: rgba("#7A4DFF", 0.3),
      borderRadius: pxToRem(999),
    },

    "& .fc .fc-daygrid-day-events::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },

    "& .fc .fc-daygrid-event": {
      margin: 0,
      border: "none",
      background: "transparent !important",
      boxShadow: "none !important",
    },

    "& .weekly-event-card": {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: pxToRem(8),
      width: "100%",
      minHeight: pxToRem(118),
      padding: `${pxToRem(14)} ${pxToRem(12)}`,
      borderRadius: pxToRem(20),
      textAlign: "center",
      background: darkMode
        ? `linear-gradient(180deg, ${rgba(white.main, 0.06)} 0%, ${rgba(white.main, 0.03)} 100%)`
        : "linear-gradient(180deg, #FCFAFF 0%, #F9F4FF 100%)",
      border: `1px solid ${rgba("#7A4DFF", darkMode ? 0.24 : 0.12)}`,
      boxShadow: `0 18px 32px ${rgba(dark.main, darkMode ? 0.18 : 0.05)}`,
    },

    "& .weekly-event-card--holiday": {
      background: darkMode
        ? `linear-gradient(180deg, ${rgba("#56C587", 0.16)} 0%, ${rgba(white.main, 0.04)} 100%)`
        : "linear-gradient(180deg, #F7FFF9 0%, #EFFBF2 100%)",
      borderColor: rgba("#56C587", darkMode ? 0.3 : 0.16),
    },

    "& .weekly-event-hero": {
      width: pxToRem(48),
      height: pxToRem(48),
      borderRadius: "50%",
      display: "grid",
      placeItems: "center",
      backgroundColor: rgba("#56C587", 0.16),
      color: "#34A86A",
    },

    "& .weekly-event-avatar": {
      width: pxToRem(46),
      height: pxToRem(46),
      fontWeight: typography.fontWeightBold,
      fontSize: pxToRem(18),
      border: `3px solid ${rgba(white.main, 0.95)}`,
      boxShadow: `0 10px 18px ${rgba(dark.main, 0.08)}`,
    },

    "& .weekly-event-avatar--birthday": {
      background: "linear-gradient(135deg, #8D63FF 0%, #B589FF 100%)",
    },

    "& .weekly-event-avatar--anniversary": {
      background: "linear-gradient(135deg, #FF9D54 0%, #FFBB74 100%)",
    },

    "& .weekly-event-title": {
      fontSize: pxToRem(12),
      fontWeight: typography.fontWeightBold,
      color: darkMode ? white.main : dark.main,
      lineHeight: 1.3,
      margin: 0,
    },

    "& .weekly-event-subtitle": {
      fontSize: pxToRem(11),
      fontWeight: typography.fontWeightRegular,
      color: darkMode ? rgba(white.main, 0.78) : grey[700],
      lineHeight: 1.2,
      margin: 0,
    },

    "& .weekly-event-chip": {
      height: pxToRem(24),
      borderRadius: pxToRem(999),
      fontWeight: typography.fontWeightBold,
      fontSize: pxToRem(10),
      "& .MuiChip-label": {
        paddingLeft: pxToRem(6),
        paddingRight: pxToRem(8),
      },
      "& .MuiChip-icon": {
        fontSize: pxToRem(13),
        marginLeft: pxToRem(6),
      },
    },

    "& .weekly-event-chip--birthday": {
      backgroundColor: darkMode ? rgba("#7A4DFF", 0.18) : "#F3ECFF",
      color: "#7A4DFF",
    },

    "& .weekly-event-chip--anniversary": {
      backgroundColor: darkMode ? rgba("#FF9E45", 0.18) : "#FFF4E7",
      color: "#FF9E45",
    },

    "& .weekly-event-chip--holiday": {
      backgroundColor: darkMode ? rgba("#56C587", 0.18) : "#EDFFF4",
      color: "#34A86A",
    },

    "& .fc .fc-weekend-cell .fc-daygrid-day-frame": {
      background: darkMode
        ? `linear-gradient(180deg, ${rgba("#8D63FF", 0.08)} 0%, ${rgba(white.main, 0.02)} 100%)`
        : "linear-gradient(180deg, #F9F4FF 0%, #FFFDFE 100%)",
      position: "relative",
      overflow: "hidden",
    },

    "& .fc .fc-weekend-cell:not(.fc-weekend-has-event) .fc-daygrid-day-events::after": {
      content: '"WEEKOFF"',
      display: "block",
      marginTop: "auto",
      textAlign: "center",
      color: "#8D63FF",
      fontSize: pxToRem(14),
      fontWeight: typography.fontWeightBold,
      letterSpacing: "0.06em",
      position: "relative",
      zIndex: 2,
    },

    "& .fc .fc-weekend-cell:not(.fc-weekend-has-event) .fc-daygrid-day-events::before": {
      content: '"Enjoy your day!"',
      display: "block",
      marginTop: pxToRem(12),
      textAlign: "center",
      color: grey[700],
      fontSize: pxToRem(12),
      fontWeight: typography.fontWeightRegular,
      position: "relative",
      zIndex: 2,
    },

    "& .fc .fc-weekend-cell:not(.fc-weekend-has-event) .fc-daygrid-day-frame::before": {
      content: '"☾"',
      position: "absolute",
      top: "22%",
      left: "50%",
      transform: "translateX(-50%)",
      fontSize: pxToRem(64),
      lineHeight: 1,
      color: rgba("#A786FF", 0.55),
      zIndex: 1,
      pointerEvents: "none",
    },

    "& .fc .fc-weekend-cell:not(.fc-weekend-has-event) .fc-daygrid-day-frame::after": {
      content: '"✦  ✧   ✦"',
      position: "absolute",
      top: "15%",
      left: "50%",
      transform: "translateX(-50%)",
      fontSize: pxToRem(14),
      letterSpacing: pxToRem(10),
      color: rgba("#B79BFF", 0.7),
      zIndex: 1,
      pointerEvents: "none",
    },

    "& .fc .fc-daygrid-body-unbalanced .fc-daygrid-day-events": {
      minHeight: 0,
    },

    "& .fc-scroller-liquid-absolute": {
      position: "static",
      overflow: "visible !important",
    },

    "& .fc-daygrid-event-harness": {
      position: "static !important",
    },

    "@media (max-width: 900px)": {
      "& .fc .fc-daygrid-day-frame": {
        height: pxToRem(216),
        minHeight: pxToRem(216),
      },

      "& .fc .fc-daygrid-day-number": {
        minWidth: pxToRem(52),
        minHeight: pxToRem(40),
        fontSize: pxToRem(11),
      },

      "& .fc .fc-daygrid-day-events": {
        gap: pxToRem(8),
        padding: `${pxToRem(8)} ${pxToRem(6)} ${pxToRem(10)}`,
      },

      "& .fc .fc-weekend-cell:not(.fc-weekend-has-event) .fc-daygrid-day-frame::before": {
        fontSize: pxToRem(48),
        top: "26%",
      },

      "& .fc .fc-weekend-cell:not(.fc-weekend-has-event) .fc-daygrid-day-frame::after": {
        fontSize: pxToRem(12),
        top: "18%",
      },

      "& .weekly-event-card": {
        minHeight: pxToRem(88),
        padding: `${pxToRem(10)} ${pxToRem(8)}`,
        borderRadius: pxToRem(16),
      },

      "& .weekly-event-avatar": {
        width: pxToRem(38),
        height: pxToRem(38),
        fontSize: pxToRem(16),
      },

      "& .weekly-event-title": {
        fontSize: pxToRem(11),
      },

      "& .weekly-event-chip": {
        height: pxToRem(22),
        fontSize: pxToRem(9),
      },
    },
  };
});
